/**
 * Profile Hook
 *
 * TanStack Query hooks for user profile operations.
 *
 * Features:
 * - Fetch user profile with caching
 * - Update profile with optimistic updates
 * - Upload avatar with progress tracking
 *
 * Reference: specs/009-user-profile/quickstart.md
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiFetchWithAuth } from '@/lib/api';

/**
 * User profile data structure (matches API response)
 */
export interface UserProfile {
  user_id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  oauth_provider: 'google' | 'github' | 'microsoft';
  language: string | null;
  timezone: string | null;
  /** Absent or null when JWT is user-scoped (multi-org chooser before picking an org). */
  organization?: {
    organization_id: string;
    name: string;
    default_language?: string;
  } | null;
  role?: string | null;
  needs_org_choice?: boolean;
  /** DC-363 — owner/admin implicitly true; otherwise membership flag from GET /users/me. */
  can_confirm_repository_scope?: boolean;
  created_at: string;
  last_login_at: string | null;
  updated_at: string | null;
}

/**
 * Profile update payload
 */
export interface ProfileUpdatePayload {
  display_name?: string;
  language?: string;
  timezone?: string;
}

/**
 * Profile API error response
 */
export interface ProfileApiError {
  error: string;
  message: string;
  details?: Array<{ path: string[]; message: string }>;
}

/**
 * Query key for profile data
 */
export const PROFILE_QUERY_KEY = ['profile', 'me'] as const;

/**
 * Fetch current user's profile
 */
async function fetchProfile(): Promise<UserProfile> {
  return api.get<UserProfile>('/users/me');
}

/**
 * Update user's profile
 */
async function updateProfileApi(
  payload: ProfileUpdatePayload,
  expectedUpdatedAt?: string
): Promise<{ message: string; profile: UserProfile }> {
  const headers: Record<string, string> = {};

  // Add optimistic locking header if provided
  if (expectedUpdatedAt) {
    headers['X-Expected-Updated-At'] = expectedUpdatedAt;
  }

  return api.patch<{ message: string; profile: UserProfile }>('/users/me', payload, {
    headers,
  });
}

/** POST /users/me/avatar JSON body (API uses camelCase). */
interface AvatarUploadApiResponse {
  avatarUrl?: string;
  avatar_url?: string;
  message?: string;
}

/**
 * Upload user's avatar
 */
async function uploadAvatarApi(file: File): Promise<{ avatar_url: string }> {
  const formData = new FormData();
  formData.append('avatar', file);

  // Note: api.post uses JSON.stringify by default; multipart uses apiFetchWithAuth (401 refresh).
  const response = await apiFetchWithAuth('/users/me/avatar', {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as ProfileApiError;
    throw new Error(errorData.message ?? `Failed to upload avatar: ${response.status}`);
  }

  const data = (await response.json()) as AvatarUploadApiResponse;
  const url = data.avatar_url ?? data.avatarUrl;
  if (!url) {
    throw new Error('Avatar upload succeeded but the server did not return an image URL.');
  }

  return { avatar_url: url };
}

/**
 * Hook to fetch user profile
 *
 * @returns Query result with profile data, loading state, and error
 */
export function useProfile() {
  return useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: fetchProfile,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to update user profile
 *
 * Implements optimistic updates with rollback on error.
 *
 * @returns Mutation for updating profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      payload,
      expectedUpdatedAt,
    }: {
      payload: ProfileUpdatePayload;
      expectedUpdatedAt?: string;
    }) => {
      return updateProfileApi(payload, expectedUpdatedAt);
    },
    // Optimistic update
    onMutate: ({ payload }) => {
      // Cancel any outgoing refetches
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.cancelQueries({ queryKey: PROFILE_QUERY_KEY });

      // Snapshot the previous value
      const previousProfile = queryClient.getQueryData<UserProfile>(PROFILE_QUERY_KEY);

      // Optimistically update to the new value
      if (previousProfile) {
        queryClient.setQueryData<UserProfile>(PROFILE_QUERY_KEY, {
          ...previousProfile,
          ...payload,
        });
      }

      return { previousProfile };
    },
    // On error, rollback
    onError: (_error, _variables, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(PROFILE_QUERY_KEY, context.previousProfile);
      }
    },
    // Always refetch after error or success
    onSettled: () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
}

/**
 * Hook to upload user avatar
 *
 * @returns Mutation for uploading avatar
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadAvatarApi,
    // Update profile cache with new avatar URL on success
    onSuccess: (data) => {
      const previousProfile = queryClient.getQueryData<UserProfile>(PROFILE_QUERY_KEY);
      if (previousProfile) {
        queryClient.setQueryData<UserProfile>(PROFILE_QUERY_KEY, {
          ...previousProfile,
          avatar_url: data.avatar_url,
        });
      }
    },
    // Always refetch after error or success
    onSettled: () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
}

/**
 * Hook to invalidate profile cache
 *
 * Use when profile may have been updated externally.
 */
export function useInvalidateProfile() {
  const queryClient = useQueryClient();

  return () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
  };
}
